import { setTaskTableMappedData } from 'common/store/reducer/commonReducer';

import {
	React,
	useEffect,
	useState,
	useCallback,
	useSelector,
	useDispatch,
	Box,
	Paper,
	Typography,
	moment,
	highIcon,
	lowIcon,
	mediumIcon,
	Loader,
	AlertComponent,
	useTableQuery,
	getTasksTableData,
	APP_GENERAL_ERROR_MSG,
	TableComponent,
	setMappingDrawer,
	setSelectedEntityTypeData,
	setSelectedTask,
	setTabIndex,
	ACTION_PLAN_TAB_CONSTANTS,
	MODULES,
	SORT_ORDER,
	HTTP_STATUS_CODES,
	getMappingIcon,
	sortFields,
	Column,
	ITaskTabContent,
	RootState,
	actionPlanStyles,
	styles,
} from './ActionPlanTaskTableModules';

const { TAB_TASKS } = ACTION_PLAN_TAB_CONSTANTS;

const ActionPlanTaskTable = (props: any) => {
	const { subPartId, sideDrawerHandler, searchTask, moduleType } = props;
	const dispatch = useDispatch();
	const [toastMessage, setToastMessageLocal] = useState('');
	const [isLoading, setLoading] = useState<boolean>(false);
	const [isToastOpen, setIsToastOpen] = useState(false);
	const [finalTaskdata, setFinalTaskData] = React.useState<ITaskTabContent[]>();
	const [totalRecordsCount, setTotalRecordCount] = useState<number>(0);
	const [totalPagesCount, setTotalPagesCount] = useState<number>(0);
	const [sortOrder, setSortOrder] = useState<string>(SORT_ORDER.ASCENDING);
	const isHorizonScanModule = moduleType === MODULES.HORIZON_SCAN_AND_ALERTS;
	const { OK } = HTTP_STATUS_CODES;
	const { tableQueryObj, updateTableQuery } = useTableQuery({
		id: 'HorizonDetailTaskTable',
	});
	const sideDrawerState = useSelector(
		(state: RootState) => state.common.mappingDrawer,
	);

	const selectedTabValue: string = useSelector(
		(state: RootState) => state?.mappingDetails?.selectedTabValue,
	);

	const taskRowData = useSelector(
		(state: RootState) => state.common.taskRowData,
	);

	useEffect(() => {
		let paginationFromOverview = {};
		if (totalRecordsCount && sideDrawerState) {
			// to get all records when navigation is from landing screen
			paginationFromOverview = { pageNumber: 0, pageSize: totalRecordsCount };
		}
		fetchTaskTable(
			Object?.keys(paginationFromOverview)?.length > 0
				? paginationFromOverview
				: tableQueryObj,
			subPartId,
			searchTask,
			sortOrder,
		);
	}, [
		sideDrawerState,
		searchTask,
		selectedTabValue,
		totalRecordsCount,
		taskRowData,
	]);

	const fetchTaskTable = async (
		data: any,
		id: string,
		searchTask: string,
		sortOrder: string,
	) => {
		const payloadData = { ...data, sortDirection: sortOrder };
		setLoading(true);
		const response = await getTasksTableData(
			id,
			payloadData,
			isHorizonScanModule,
			searchTask,
		);
		if (response?.statusCode === OK) {
			const responseData = response?.responseBody;
			const tempTableData = response?.responseBody?.content ?? [];
			dispatch(setTaskTableMappedData(tempTableData));
			if (tempTableData.length > 0) {
				setFinalTaskData(tempTableData);
			} else {
				setFinalTaskData([]);
			}
			setTotalRecordCount(responseData?.totalCount ?? 0);
			setTotalPagesCount(responseData?.totalPages ?? 0);
			setLoading(false);
		} else if (response?.statusCode === 404) {
			dispatch(setTaskTableMappedData([]));
			setFinalTaskData([]);
			setLoading(false);
		} else {
			dispatch(setTaskTableMappedData([]));
			setIsToastOpen(true);
			setToastMessageLocal(APP_GENERAL_ERROR_MSG);
			setFinalTaskData([]);
			setLoading(false);
		}
	};

	const getRiskImage = (label: string) => {
		// Define a mapping of risk labels to their respective icon sources
		const riskIconMap: { [key: string]: string } = {
			High: highIcon,
			Low: lowIcon,
			Medium: mediumIcon,
		};

		// Check if the label exists in the mapping
		if (label in riskIconMap) {
			// Return the corresponding icon image
			return (
				<img
					src={riskIconMap[label]}
					alt={`${label} icon`}
					className={styles['risk-table-cell-icon']}
				/>
			);
		}
		// Return null if no matching icon is found
		return null;
	};

	const handleToast = useCallback(() => {
		setIsToastOpen(false);
		setToastMessageLocal('');
	}, []);

	const handlePageChange = (data: any, flag?: boolean) => {
		if (flag) {
			updateTableQuery(data);
			fetchTaskTable(data, subPartId, searchTask, sortOrder);
		}
	};

	const handleSortChange = (data: any) => {
		sortFields(sortOrder, setSortOrder);
		fetchTaskTable(data, subPartId, searchTask, sortOrder);
	};

	const columns: readonly Column[] = [
		{
			id: 'taskName',
			label: 'Task Name',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Typography
					component="div"
					data-testid="taskId-link"
					className={styles['remediation-table-task-name']}
					onClick={() => onTaskNameClickHandler(params)}
				>
					{params?.taskName}
				</Typography>
			),
		},
		{
			id: 'mappedTo',
			label: 'Mapped to',
			disablePadding: false,
			render: (params: any) => (
				<Typography component="div" data-testid="taskname-link">
					<span className={styles['object-icon-spacing']}>
						{params?.mappedTo && getMappingIcon(params?.objectType) ? (
							<div
								className={`${
									actionPlanStyles[`${getMappingIcon(params?.objectType)}`]
								} ${actionPlanStyles['circle-icon']}`}
							/>
						) : null}
					</span>
					{params?.mappedTo}
				</Typography>
			),
		},
		{
			id: 'targetEndDate',
			label: 'Deadline',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Box>
					{params.deadLine ? moment(params.deadLine).format('MM/DD/YYYY') : ''}
				</Box>
			),
		},
		{
			id: 'taskImportance',
			label: 'Impact Level',
			isSortable: true,
			disablePadding: false,
			render: (params: any) => (
				<Typography className={styles['remediation-table-non-link-column']}>
					<span className={styles['icon-spacing']}>
						{getRiskImage(params.impactLevel)}
					</span>
					<span>{params?.impactLevel}</span>
				</Typography>
			),
		},

		{
			id: 'assignedUserFirstName',
			label: 'Assignee',
			disablePadding: false,
			render: (params: any) => (
				<Typography
					component="div"
					className={styles['remediation-table-non-link-column']}
				>
					{`${params?.assignedUserFirstName} ${params?.assignedUserLastName}`}
				</Typography>
			),
		},
		{
			id: 'status',
			label: 'Status',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Typography
					component="div"
					className={styles['remediation-table-non-link-column']}
				>
					{params?.status}
				</Typography>
			),
		},
	];

	const onTaskNameClickHandler = (data: any) => {
		// open tasks tab by default on click of task item
		dispatch(setMappingDrawer(!sideDrawerState));
		dispatch(setTabIndex(TAB_TASKS));
		dispatch(
			setSelectedEntityTypeData({
				id: data.mappedToId,
				title: data.mappedTo,
				type: data.objectType.toLowerCase(),
			}),
		);
		dispatch(setSelectedTask(data.id));
		sideDrawerHandler();
	};

	return (
		<Box data-testid="horizon-task-tab" className={styles['task-table-box']}>
			{finalTaskdata && finalTaskdata.length > 0 ? (
				<TableComponent
					defaultQuery={tableQueryObj}
					rows={finalTaskdata}
					columns={columns}
					totalItems={totalRecordsCount}
					defaultSortKey=""
					onPageHandler={handlePageChange}
					isLoading={isLoading}
					tableStyles={{ minWidth: '46.875em', maxHeight: '27.5em' }}
					totalPagesCount={totalPagesCount}
					enableHeaderColor
					onSortHandler={handleSortChange}
					isRenderPagination
				/>
			) : (
				<div>
					{!isLoading ? (
						<Paper className={styles['remediation-plan-mapping-loading-box']}>
							<Typography data-testid="no-data-found">No data found</Typography>
						</Paper>
					) : (
						<Paper className={styles['remediation-plan-mapping-loading-box']}>
							<Loader show={isLoading} />
						</Paper>
					)}
				</div>
			)}
			<AlertComponent
				open={isToastOpen}
				toastMessage={toastMessage}
				severity="error"
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</Box>
	);
};
export default ActionPlanTaskTable;
